// Settings control factories. Each control reads/writes the shared store, which
// broadcasts changes to the app tab (applied live). Controls also SUBSCRIBE to the
// store so they re-sync their displayed value when a setting changes elsewhere -
// applying a bundle, a reset, or a change in another tab - without clobbering an
// input the user is actively editing.
import { get, set, subscribe } from "../shared/settings-store.js";

export function section(title) {
	const s = document.createElement("section");
	s.className = "rv-section";
	const h = document.createElement("h2");
	h.className = "rv-section-title";
	h.textContent = title;
	const body = document.createElement("div");
	body.className = "rv-section-body";
	s.append(h, body);
	s.body = body;
	return s;
}

// A sub-heading that divides a section body into labeled groups of rows.
export function group(title) {
	const g = document.createElement("div");
	g.className = "rv-group";
	g.textContent = title;
	return g;
}

let rowSeq = 0;
export function row(label, desc, control) {
	const r = document.createElement("div");
	r.className = "rv-row";
	const t = document.createElement("div");
	t.className = "rv-row-text";
	const l = document.createElement("div");
	l.className = "rv-row-label";
	l.textContent = label;
	l.id = "rv-row-" + ++rowSeq;
	t.appendChild(l);
	let d = null;
	if (desc) {
		d = document.createElement("div");
		d.className = "rv-row-desc";
		d.textContent = desc;
		d.id = l.id + "-d";
		t.appendChild(d);
	}
	const c = document.createElement("div");
	c.className = "rv-row-control";
	if (control) c.appendChild(control);
	// Switches, selects and inputs carry no text of their own, so screen readers would
	// announce "switch, on" with no idea what it is. Name them after the row.
	if (control) for (const el of c.querySelectorAll('[role="switch"], [role="radiogroup"], select, input:not([type="button"]):not([type="submit"]):not([type="hidden"]):not([type="radio"]):not([type="checkbox"]), textarea')) {
		if (el.hasAttribute("aria-label") || el.hasAttribute("aria-labelledby")) continue;
		el.setAttribute("aria-labelledby", l.id);
		if (d && !el.hasAttribute("aria-describedby")) el.setAttribute("aria-describedby", d.id);
	}
	r.append(t, c);
	return r;
}

export function toggle(key) {
	const b = document.createElement("button");
	b.className = "rv-switch";
	b.type = "button";
	b.setAttribute("role", "switch");
	const sync = () => { const v = !!get(key); b.dataset.on = v ? "1" : "0"; b.setAttribute("aria-checked", String(v)); };
	b.addEventListener("click", () => { set(key, !get(key)); sync(); });
	subscribe(sync);
	sync();
	return b;
}

export function select(key, options) {
	const s = document.createElement("select");
	s.className = "rv-select";
	options.forEach(([val, label]) => { const o = document.createElement("option"); o.value = val; o.textContent = label; s.appendChild(o); });
	s.value = get(key);
	s.addEventListener("change", () => set(key, s.value));
	subscribe(() => { const v = String(get(key)); if (s.value !== v) s.value = v; });
	return s;
}

// Like select() but options are split into <optgroup>s. `groups` is
// [{ label, options: [[value, label], …] }]; a falsy group label is ungrouped.
export function groupedSelect(key, groups) {
	const s = document.createElement("select");
	s.className = "rv-select";
	groups.forEach((g) => {
		const target = g.label ? document.createElement("optgroup") : s;
		if (g.label) { target.label = g.label; s.appendChild(target); }
		g.options.forEach(([val, label]) => { const o = document.createElement("option"); o.value = val; o.textContent = label; target.appendChild(o); });
	});
	s.value = get(key);
	s.addEventListener("change", () => set(key, s.value));
	subscribe(() => { const v = String(get(key)); if (s.value !== v) s.value = v; });
	return s;
}

// Like select() but each option renders in its own typeface (font preview).
// `fonts` is the FONTS registry { id: { label, stack } }.
export function fontSelect(key, fonts) {
	const s = document.createElement("select");
	s.className = "rv-select rv-font-select";
	Object.entries(fonts).forEach(([id, f]) => {
		const o = document.createElement("option");
		o.value = id; o.textContent = f.label; o.style.fontFamily = f.stack;
		s.appendChild(o);
	});
	s.value = get(key);
	s.addEventListener("change", () => set(key, s.value));
	subscribe(() => { const v = String(get(key)); if (s.value !== v) s.value = v; });
	return s;
}

export function textInput(key, placeholder) {
	const i = document.createElement("input");
	i.className = "rv-input";
	i.type = "text";
	i.spellcheck = false;
	i.placeholder = placeholder || "";
	i.value = get(key) || "";
	i.addEventListener("change", () => set(key, i.value));
	subscribe(() => { if (document.activeElement !== i) i.value = get(key) || ""; });
	return i;
}

// Slider with a live value readout. `fmt(value)` formats the readout (e.g. px/%).
export function range(key, min, max, step, fmt) {
	const wrap = document.createElement("div");
	wrap.className = "rv-range";
	const input = document.createElement("input");
	input.type = "range";
	input.min = String(min);
	input.max = String(max);
	input.step = String(step || 1);
	input.value = String(get(key));
	const out = document.createElement("span");
	out.className = "rv-range-val";
	const render = () => { out.textContent = fmt ? fmt(input.value) : String(input.value); };
	input.addEventListener("input", () => { set(key, Number(input.value)); render(); });
	subscribe(() => { const v = String(get(key)); if (input.value !== v && document.activeElement !== input) { input.value = v; render(); } });
	render();
	wrap.append(input, out);
	return wrap;
}

// Spoken names for the preset swatches (a hex code means nothing read aloud).
const COLOR_NAMES = { "#f6b93b": "Amber", "#e9c46a": "Gold", "#ff8a3d": "Orange", "#ff5a5a": "Red", "#ff5d8f": "Pink", "#b388ff": "Lavender", "#818cf8": "Indigo", "#4f9cff": "Blue", "#38bdf8": "Sky blue", "#2dd4bf": "Teal", "#27c093": "Green", "#a3e635": "Lime", "#e6e6e6": "Silver" };

export function color(key, presets) {
	const wrap = document.createElement("div");
	wrap.className = "rv-color";
	const inp = document.createElement("input");
	inp.type = "color";
	inp.value = get(key);
	inp.addEventListener("input", () => set(key, inp.value));
	subscribe(() => { const v = get(key); if (inp.value !== v && document.activeElement !== inp) inp.value = v; });
	wrap.appendChild(inp);
	(presets || []).forEach((c) => {
		const b = document.createElement("button");
		b.type = "button";
		b.className = "rv-swatch";
		b.style.background = c;
		b.title = COLOR_NAMES[c] || c;
		b.setAttribute("aria-label", COLOR_NAMES[c] || c);
		b.addEventListener("click", () => { set(key, c); inp.value = c; });
		wrap.appendChild(b);
	});
	const mark = () => { const v = String(get(key) || "").toLowerCase(); wrap.querySelectorAll(".rv-swatch").forEach((b, i) => b.setAttribute("aria-pressed", String(presets[i].toLowerCase() === v))); };
	mark();
	subscribe(mark);
	return wrap;
}

export function button(label, onClick, cls) {
	const b = document.createElement("button");
	b.type = "button";
	b.className = "rv-btn" + (cls ? " " + cls : "");
	b.textContent = label;
	b.addEventListener("click", onClick);
	return b;
}

export function locked(text) {
	const d = document.createElement("div");
	d.className = "rv-locked";
	d.innerHTML = '<svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.4"><rect x="3.5" y="7" width="9" height="6.5" rx="1"></rect><path d="M5.5 7 V5 a2.5 2.5 0 0 1 5 0 V7"></path></svg><span></span>';
	d.querySelector("span").textContent = text;
	return d;
}

// A set of radio buttons for one choice. value() reads the current choice, pick(v) saves one.
// Arrow keys move and choose, like native radios (they are native radios).
let radioSeq = 0;
export function radios(name, options, value, pick) {
	const wrap = document.createElement("div");
	wrap.className = "rv-radios";
	wrap.setAttribute("role", "radiogroup");
	const group = "rv-radio-" + ++radioSeq;
	const inputs = options.map(([v, label]) => {
		const l = document.createElement("label");
		l.className = "rv-radio";
		const i = document.createElement("input");
		i.type = "radio";
		i.name = group;
		i.value = v;
		i.addEventListener("change", () => { if (i.checked) pick(v); });
		const s = document.createElement("span");
		s.textContent = label;
		l.append(i, s);
		wrap.appendChild(l);
		return i;
	});
	const sync = () => { const cur = value(); inputs.forEach((i) => { i.checked = i.value === cur; }); };
	sync();
	subscribe(sync);
	wrap.dataset.name = name;
	return wrap;
}

// A plain line of text for "info" rows.
export function note(text, cls) {
	const d = document.createElement("div");
	d.className = "rv-row-desc" + (cls ? " " + cls : "");
	d.textContent = text;
	return d;
}
